import { AnyAction, configureStore, Dispatch, EnhancedStore, Middleware, MiddlewareArray } from '@reduxjs/toolkit'
import localeReducer, { LocaleState } from './slices/locale';
import sessionReducer, { SessionState } from './slices/session';
import alarmGroupReducer, { AlarmGroupState } from './slices/alarmGroup';
import organizationUserReducer, { OrganizationUserState } from './slices/organizationUser';
import accountCreationReducer, { AccountCreationState } from './slices/accountCreation';
import inviteSummaryReducer, { InviteSummaryState } from './slices/inviteSummary';
import { API } from '../types/apiv1';
import { ThunkDispatch } from 'redux-thunk';

import { combineReducers } from 'redux'
import { persistReducer } from 'redux-persist'
import storage from 'redux-persist/lib/storage' 
import { NavigateFunction } from 'react-router-dom';

export type State = {
	locale: LocaleState,
	session: SessionState,
	alarmGroup: AlarmGroupState,
	organizationUsers: OrganizationUserState,
	accountCreation: AccountCreationState,
	inviteSummary: InviteSummaryState,	
};

export type Store = EnhancedStore<State, AnyAction, MiddlewareArray<Middleware<{}, State>>>;

export type ExtraArgument = { api: API, navigate: NavigateFunction };

const persistConfig = {
	key: 'root',
	storage,
	//whitelist session and locale state to persist
	whitelist: ['session','locale'],
	//this doesnt blacklist the entire session state, but only the nested states defined in unauthConfig
	blacklist: ['session']
};

const unauthConfig = {
	key: 'session',
	storage,
	blacklist: ['volatile'],
};

const rootReducer = combineReducers({
	locale: localeReducer,
	session: persistReducer(unauthConfig,sessionReducer),
	alarmGroup: alarmGroupReducer,
	organizationUsers: organizationUserReducer,
	accountCreation: accountCreationReducer,
	inviteSummary: inviteSummaryReducer,
});

const persistedReducer = persistReducer(persistConfig, rootReducer);

export function newStore(api: API, navigate: NavigateFunction): Store {
	return configureStore({
		reducer: persistedReducer,
		middleware: getDefaultMiddleware =>
			getDefaultMiddleware({
				//The redux-persist middleware internally uses non-serializable data, causing warning to fire. 
				//Disable the warning as we can't do anything about that
				serializableCheck: false,
				thunk: {
					extraArgument: { api, navigate },
				},
			}),
	});
}

export type GetAppState = () => State;
export type AppDispatch = ThunkDispatch<State, ExtraArgument, AnyAction> & Dispatch;
